import { useCallback } from 'react';

import { useJsonVariation, useBoolVariation } from '@launchdarkly/react-sdk';

import { IS_PROD } from '../../utils/env-util';
import { useHasPermission } from '../useHasPermission';

/**
 * Returns a function that tells whether the user may edit BOMs for a given brand.
 * Use this instead of `useBomAccess` when several brands need to be checked.
 */
const useCanEditBomForBrand = (): ((
    brandNumber: string | null | undefined
) => boolean) => {
    const hasEditPermission = useHasPermission('EDIT_BOM');

    const brandNumbersLiveWithBom = useJsonVariation<Array<string>>(
        'brands-live-with-bom',
        []
    );

    // For brands that are not live yet, you can use the bom flag
    const hasBomFlag = useBoolVariation('bom-section', false);

    return useCallback(
        (brandNumber: string | null | undefined): boolean => {
            if (!IS_PROD) {
                // On dev and test, we always allow BOM access
                return true;
            }

            if (!brandNumber) {
                // Without brand number, you can never edit
                return false;
            }

            // On prod, first check the edit permission, then the brand, and then the
            if (!hasEditPermission) {
                return false;
            }

            if (brandNumbersLiveWithBom.includes(brandNumber)) {
                // We always allow bom editing if the brand is live
                return true;
            }

            // Before go live we block BOM except when the user has the flag + permission (buying + beta testers)
            return hasBomFlag;
        },
        [hasEditPermission, brandNumbersLiveWithBom, hasBomFlag]
    );
};

const useBomAccess = (brandNumber: string | null | undefined): boolean => {
    const canEditBomForBrand = useCanEditBomForBrand();

    return canEditBomForBrand(brandNumber);
};

export { useBomAccess, useCanEditBomForBrand };
